import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { filter, finalize, takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/core/app.state';
import { CommonService } from 'src/app/shared/common.service';
import { IBreadcrumb } from 'src/app/ui/page-header/page-header.component';
import {
  ICreateWorkflowRuleDTO,
  IWorkflowRule,
  IWorkflowSchema,
  IWorkflowTemplate,
  WorkflowRuleStatus,
} from '../../models/workflows.models';
import { WorkflowTemplatesService } from '../../services/workflow-templates.service';
import { WorkflowsService } from '../../services/workflows.service';
import * as workflowsActions from '../../store/workflows.actions';
import {
  selectWorkflowSaveCompletedAt,
  selectWorkflowSaveLoading,
  selectWorkflowSchema,
  selectWorkflowTemplates,
} from '../../store/workflows.selectors';
import { WorkflowBuilderStepComponent } from '../workflow-builder-step/workflow-builder-step.component';

// ─────────────────────────────────────────────────────────────────────────────
// WorkflowsNewRulePageComponent
// ─────────────────────────────────────────────────────────────────────────────
// Full-page replacement for the former new-rule dialog. Handles both creation
// (route `workflows/new-rule`) and editing (route `workflows/edit-rule/:id`).
// Step 0 is the entry screen (AI prompt / build from scratch); step 1 hosts the
// WHEN / IF / THEN builder. Saving navigates back to the rules list.
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_TAG_TYPES: Record<WorkflowRuleStatus, string> = {
  draft: 'secondary',
  active: 'success',
  paused: 'warning',
  archived: 'secondary',
};

@Component({
  selector: 'app-workflows-new-rule-page',
  templateUrl: './workflows-new-rule-page.component.html',
  styleUrls: ['./workflows-new-rule-page.component.scss'],
})
export class WorkflowsNewRulePageComponent implements OnInit, OnDestroy {
  @ViewChild(WorkflowBuilderStepComponent) builder?: WorkflowBuilderStepComponent;

  readonly saving$: Observable<boolean> = this.store.select(selectWorkflowSaveLoading);

  /** Backend-owned template catalog for the step-0 "start from template" section. */
  readonly templates$: Observable<IWorkflowTemplate[]> = this.store.select(selectWorkflowTemplates);

  readonly aiDescriptionControl = new FormControl<string>('', { nonNullable: true });

  activeStep: 0 | 1 = 0; // 0 = entry (type selection), 1 = builder
  mode: 'create' | 'edit' = 'create';
  aiGenerating = false;
  origin: 'manual' | 'template' | 'ai' | 'edit' | null = null;
  rule: Partial<IWorkflowRule> = {};
  schema: IWorkflowSchema | null = null;
  breadcrumbs: IBreadcrumb[] = [];

  private editRuleId: string | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private router: Router,
    private workflowTemplatesService: WorkflowTemplatesService,
    private workflowsService: WorkflowsService,
    private commonService: CommonService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(workflowsActions.ResetWorkflowSaveState());

    this.store.select(selectWorkflowSchema)
      .pipe(takeUntil(this.destroy$))
      .subscribe((schema) => {
        if (schema) {
          this.schema = schema;
        }
      });
    this.store.dispatch(workflowsActions.GetWorkflowSchema());
    this.store.dispatch(workflowsActions.GetWorkflowTemplates());

    this.store.select(selectWorkflowSaveCompletedAt)
      .pipe(
        takeUntil(this.destroy$),
        filter((completedAt): completedAt is number => completedAt !== null),
      )
      .subscribe(() => this.finishSave());

    this.editRuleId = this.route.snapshot.paramMap.get('id');
    if (this.editRuleId) {
      this.loadRuleForEdit(this.editRuleId);
    } else {
      this.setBreadcrumbs();
    }
  }

  ngOnDestroy(): void {
    this.store.dispatch(workflowsActions.ResetWorkflowSaveState());
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Edit mode shows a loader until both the rule and the schema have arrived,
   *  so the step-0 entry screen never flashes while the builder is still loading. */
  get isLoading(): boolean {
    return this.mode === 'edit' && !(this.activeStep === 1 && !!this.schema);
  }

  // ── Header ──────────────────────────────────────────────────────────────

  get statusTagLabel(): string {
    const status = this.rule.status ?? 'draft';
    return this.translate.instant(`workflows.builder.status.${status}`);
  }

  get statusTagType(): string {
    return STATUS_TAG_TYPES[this.rule.status ?? 'draft'];
  }

  // ── Step 0: Entry ─────────────────────────────────────────────────────────

  get aiButtonLabel(): string {
    const key = this.aiGenerating
      ? 'workflows.new_rule.ai.generating'
      : 'workflows.new_rule.ai.generate';
    return this.translate.instant(key);
  }

  generateWithAi(): void {
    const prompt = this.aiDescriptionControl.value.trim();

    if (this.aiGenerating || !prompt) {
      return;
    }

    this.aiGenerating = true;
    this.workflowsService.generateRuleWithAi(prompt)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.aiGenerating = false)),
      )
      .subscribe({
        next: (draft) => {
          this.origin = 'ai';
          this.rule = draft;
          this.activeStep = 1;
        },
        error: (err) => {
          const message = err?.status === 501
            ? this.translate.instant('workflows.new_rule.ai.coming_soon')
            : this.translate.instant('workflows.new_rule.ai.error');
          this.commonService.showToast(message, 'WARNING');
        },
      });
  }

  buildManually(): void {
    this.origin = 'manual';
    this.rule = this.workflowTemplatesService.buildEmptyRule();
    this.activeStep = 1;
  }

  /** Start from a template: materialise it as an ordinary editable rule draft.
   *
   *  The name is prefilled from the template so the rule is immediately savable.
   *  Which parts render as locked is derived by the builder from the schema — the
   *  template payload carries no lock metadata. */
  startFromTemplate(template: IWorkflowTemplate): void {
    this.origin = 'template';
    this.rule = this.workflowTemplatesService.buildRuleFromTemplate(
      template,
      this.translate.instant(template.i18nDefaultName),
    );
    this.activeStep = 1;
  }

  trackByTemplateKey(_index: number, template: IWorkflowTemplate): string {
    return template.key;
  }

  // ── Step 1: Builder ─────────────────────────────────────────────────────

  onRuleChange(updated: Partial<IWorkflowRule>): void {
    this.rule = updated;
  }

  /** Triggered by the header buttons; delegates validation to the builder. */
  triggerSave(targetStatus: WorkflowRuleStatus): void {
    this.builder?.onSave(targetStatus);
  }

  onBuilderSave(event: { targetStatus: WorkflowRuleStatus }): void {
    if (!this.rule.name || !this.rule.trigger) {
      return;
    }

    const dto: ICreateWorkflowRuleDTO = {
      name: this.rule.name,
      description: this.rule.description ?? null,
      trigger: this.rule.trigger,
      conditionGroups: this.rule.conditionGroups ?? [],
      actions: this.rule.actions ?? [],
      stopProcessing: this.rule.stopProcessing ?? false,
    };

    this.store.dispatch(workflowsActions.SaveWorkflowRule({
      id: this.mode === 'edit' ? this.editRuleId ?? undefined : undefined,
      dto,
      targetStatus: event.targetStatus,
    }));
  }

  cancel(): void {
    this.router.navigate(['/workflows']);
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private setBreadcrumbs(): void {
    this.breadcrumbs = [
      { label: this.translate.instant('workflows.title'), link: '/workflows' },
    ];
  }

  private loadRuleForEdit(id: string): void {
    this.mode = 'edit';
    this.origin = 'edit';
    this.setBreadcrumbs();
    this.workflowsService.getWorkflowRule(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((fullRule) => {
        this.rule = { ...fullRule };
        this.activeStep = 1;
      });
  }

  private finishSave(): void {
    this.store.dispatch(workflowsActions.ResetWorkflowSaveState());
    this.router.navigate(['/workflows']);
  }
}
